import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, FileText, Library, CalendarDays, Mail, MailOpen, Copy, Pencil, X, Save, Eye, DollarSign } from "lucide-react";
import {
  getCoachingAthlete,
  listPrograms,
  createProgram,
  deleteProgram,
  deleteCoachingAthlete,
  cloneProgram,
  updateCoachingAthlete,
  getLastDigest,
  getPortalStatusesBulk,
  getNextDigest,
  getDailyPreviewUrl,
  getForgotLogPreviewUrl,
  getAthleteLastSeen,
  getCoachingAthleteLactate,
  coachListWorkoutShifts,
} from "../../lib/api";
import { toast } from "sonner";
import StravaPanel from "../../components/coaching/StravaPanel";
import RaceCalendar from "../../components/coaching/RaceCalendar";
import PaceProfile from "../../components/coaching/PaceProfile";
import LactateProfile from "../../components/coaching/LactateProfile";
import CalendarFeedInspector from "../../components/coaching/CalendarFeedInspector";
import AthleteActivityFeed from "../../components/coaching/AthleteActivityFeed";
import FourWeekOverview from "../../components/coaching/FourWeekOverview";
import AssignProgramModal from "../../components/coaching/AssignProgramModal";
import InviteAthleteModal from "../../components/coaching/InviteAthleteModal";
import PaymentStatusBadge from "../../components/coaching/PaymentStatusBadge";
import AthleteConversationsSection from "../../components/coaching/AthleteConversationsSection";
import CoachAthleteProgress from "../../components/coaching/CoachAthleteProgress";
import { Link2 } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtAgo(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const ms = Date.now() - d.getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return "yesterday";
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function DigestStatusLine({ last }) {
  if (!last) {
    return (
      <span className="text-[9px] tracking-[0.16em] uppercase text-[var(--tir-muted)] font-normal pl-6 whitespace-nowrap"
        data-testid="digest-status-line">
        — never sent
      </span>
    );
  }
  const status = last.status || (last.opened_at ? "opened" : last.delivered_at ? "delivered" : "sent");
  const colour =
    status === "opened" || status === "clicked"
      ? "text-emerald-700"
      : status === "bounced" || status === "complained" || status === "failed"
      ? "text-[var(--tir-burgundy)]"
      : "text-[var(--tir-navy)]";
  return (
    <span
      className="text-[9px] tracking-[0.16em] uppercase font-normal pl-6 inline-flex items-center gap-1 whitespace-nowrap"
      data-testid="digest-status-line"
    >
      <span className="text-[var(--tir-muted)]">W{last.week_number} · {fmtAgo(last.sent_at)} ·</span>
      <span className={`${colour} font-semibold`}>{status}</span>
    </span>
  );
}

function NextDigestLine({ next }) {
  if (!next) return null;
  if (next.enabled === false) {
    const reasons = {
      no_email: "needs email",
      weekly_disabled: "auto-email off",
      portal_inactive: "athlete hasn't activated portal",
    };
    const txt = reasons[next.reason];
    if (!txt) return null;
    return (
      <span
        className="text-[9px] tracking-[0.16em] uppercase text-[var(--tir-muted)] italic font-normal pl-6 whitespace-nowrap"
        data-testid="next-digest-line"
      >
        next: paused — {txt}
      </span>
    );
  }
  if (next.reason === "no_future_weeks" || !next.next_send_at) {
    return (
      <span
        className="text-[9px] tracking-[0.16em] uppercase text-[var(--tir-muted)] italic font-normal pl-6 whitespace-nowrap"
        data-testid="next-digest-line"
      >
        next: nothing scheduled
      </span>
    );
  }
  let pretty = next.next_send_at;
  try {
    const d = new Date(next.next_send_at);
    const opts = {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    };
    if (next.tz) opts.timeZone = next.tz;
    pretty = d.toLocaleString(undefined, opts);
  } catch {
    /* ignore */
  }
  return (
    <span
      className="text-[9px] tracking-[0.16em] uppercase text-[var(--tir-muted)] font-normal pl-6 inline-flex items-center gap-1 whitespace-nowrap"
      data-testid="next-digest-line"
      title={`Auto-email for Week ${next.week_number}${next.program_title ? ` · ${next.program_title}` : ""}`}
    >
      next: <span className="text-[var(--tir-ink)] font-semibold">{pretty}</span>
      {next.week_number ? <span className="text-[var(--tir-burgundy)] font-semibold">· W{next.week_number}</span> : null}
    </span>
  );
}

/**
 * Small chip next to the athlete's name showing when they last hit the
 * portal. Color escalates as the gap grows so ghosts are easy to spot:
 *   - just now / today / yesterday   → ink
 *   - 2-7 days                       → muted
 *   - 8-29 days                      → amber
 *   - 30+ days OR never              → burgundy
 */
function LastSeenChip({ data }) {
  if (!data) return null;
  if (!data.ever_seen) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase font-semibold px-1.5 py-0.5 border border-[var(--tir-burgundy)]/40 bg-[var(--tir-burgundy)]/10 text-[var(--tir-burgundy)] whitespace-nowrap"
        data-testid="last-seen-chip"
        title="This athlete hasn't logged into the portal yet"
      >
        Never signed in
      </span>
    );
  }
  const d = Number(data.days_ago);
  let label;
  if (!Number.isFinite(d)) label = "Active";
  else if (d < 1 / 24) label = "Just now";
  else if (d < 1) label = "Today";
  else if (d < 2) label = "Yesterday";
  else if (d < 30) label = `${Math.floor(d)}d ago`;
  else if (d < 365) label = `${Math.floor(d / 30)}mo ago`;
  else label = `${Math.floor(d / 365)}y ago`;
  let cls = "border-[var(--tir-border)] bg-white text-[var(--tir-ink)]";
  if (d >= 30) cls = "border-[var(--tir-burgundy)]/40 bg-[var(--tir-burgundy)]/10 text-[var(--tir-burgundy)]";
  else if (d >= 8) cls = "border-amber-600/40 bg-amber-100/50 text-amber-800";
  else if (d >= 2) cls = "border-[var(--tir-border)] bg-white text-[var(--tir-muted)]";
  let pretty = data.last_seen_at;
  try {
    pretty = new Date(data.last_seen_at).toLocaleString();
  } catch {
    /* ignore */
  }
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase font-semibold px-1.5 py-0.5 border whitespace-nowrap ${cls}`}
      data-testid="last-seen-chip"
      title={`Last portal activity: ${pretty}`}
    >
      Seen {label}
    </span>
  );
}

export default function CoachingAthleteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [togglingPaid, setTogglingPaid] = useState(false);
  const [savingEmailToggle, setSavingEmailToggle] = useState(false);
  const [lastDigest, setLastDigest] = useState(null);
  const [portalStatus, setPortalStatus] = useState(null); // 'active' | 'invited' | null (= draft)
  const [nextDigest, setNextDigest] = useState(null);
  // When a matching lactate test exists for this athlete (by email), the
  // program editor auto-uses lactate-derived training paces. We surface that
  // with a small badge next to the email so the coach can see at a glance
  // that the link is active without having to open the editor.
  const [lactateLink, setLactateLink] = useState(null);
  // Portal usage signal — when this athlete last hit the portal. Drives the
  // small "Last seen X ago" chip next to their name so the coach can spot
  // ghosts without opening the activity feed.
  const [lastSeen, setLastSeen] = useState(null);
  // Inline profile editor: when `editing` is true, render an edit panel that
  // lets the coach update name / email / phone / goal / target_race in place.
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const openEdit = () => {
    setEditForm({
      name: athlete?.name || "",
      email: athlete?.email || "",
      phone: athlete?.phone || "",
      goal: athlete?.goal || "",
      target_race: athlete?.target_race || "",
      notes: athlete?.notes || "",
      payment_link: athlete?.payment_link || "",
      payment_note: athlete?.payment_note || "",
      paid_active: athlete?.paid_active !== false,
    });
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setEditForm(null);
  };
  const saveProfile = async () => {
    if (!editForm) return;
    const trimmedEmail = (editForm.email || "").trim();
    // Light client-side email sanity check so we don't ship obviously-broken
    // addresses to Resend.
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error("That email doesn't look right — please double-check.");
      return;
    }
    setSavingProfile(true);
    try {
      const payload = {
        name: editForm.name?.trim() || athlete?.name,
        email: trimmedEmail || null,
        phone: (editForm.phone || "").trim() || null,
        goal: (editForm.goal || "").trim() || null,
        target_race: (editForm.target_race || "").trim() || null,
        notes: (editForm.notes || "").trim() || null,
        payment_link: (editForm.payment_link || "").trim() || null,
        payment_note: (editForm.payment_note || "").trim() || null,
        paid_active: editForm.paid_active !== false,
      };
      const updated = await updateCoachingAthlete(id, payload);
      setAthlete(updated);
      toast.success("Profile updated");
      setEditing(false);
      setEditForm(null);
    } catch (e) {
      toast.error("Couldn't save — try again");
    } finally {
      setSavingProfile(false);
    }
  };

  const onToggleWeeklyEmail = async () => {
    if (!athlete) return;
    const next = !(athlete.weekly_email_enabled !== false); // default true
    setSavingEmailToggle(true);
    try {
      const updated = await updateCoachingAthlete(id, { weekly_email_enabled: next });
      setAthlete(updated);
      toast.success(next ? "Weekly preview email: ON" : "Weekly preview email: OFF");
      getNextDigest(id).then(setNextDigest).catch(() => setNextDigest(null));
    } catch {
      toast.error("Couldn't update preference");
    } finally {
      setSavingEmailToggle(false);
    }
  };

  const onToggleDailyEmail = async () => {
    if (!athlete) return;
    const next = !athlete.daily_email_enabled; // default false
    setSavingEmailToggle(true);
    try {
      const updated = await updateCoachingAthlete(id, { daily_email_enabled: next });
      setAthlete(updated);
      toast.success(next ? "Daily workout email: ON" : "Daily workout email: OFF");
    } catch {
      toast.error("Couldn't update preference");
    } finally {
      setSavingEmailToggle(false);
    }
  };

  // Persist a "HH:MM" override (null clears it = use default 08:00).
  const onChangeDailyTime = async (value) => {
    const cleaned = (value || "").trim();
    setSavingEmailToggle(true);
    try {
      const updated = await updateCoachingAthlete(id, {
        daily_email_time: cleaned ? cleaned : null,
      });
      setAthlete(updated);
    } catch {
      toast.error("Couldn't update send time");
    } finally {
      setSavingEmailToggle(false);
    }
  };

  // Forgot-to-log end-of-week reminder. Fires at 7 PM athlete-local on the
  // day they pick (default Sunday). Skipped silently if everything's logged.
  const onToggleForgotLog = async () => {
    if (!athlete) return;
    const next = !athlete.forgot_log_reminder_enabled;
    setSavingEmailToggle(true);
    try {
      const updated = await updateCoachingAthlete(id, {
        forgot_log_reminder_enabled: next,
      });
      setAthlete(updated);
      toast.success(next ? "Forgot-to-log reminder: ON" : "Forgot-to-log reminder: OFF");
    } catch {
      toast.error("Couldn't update preference");
    } finally {
      setSavingEmailToggle(false);
    }
  };

  // Which weekday the reminder fires on (0=Mon..6=Sun). The athlete's
  // "last day of the week" depends on their preferred schedule.
  const onChangeForgotLogDay = async (value) => {
    const day = Math.max(0, Math.min(6, parseInt(value, 10)));
    setSavingEmailToggle(true);
    try {
      const updated = await updateCoachingAthlete(id, {
        forgot_log_reminder_day: day,
      });
      setAthlete(updated);
    } catch {
      toast.error("Couldn't update reminder day");
    } finally {
      setSavingEmailToggle(false);
    }
  };

  // Per-athlete "drip release" — how many weeks of the program are unlocked
  // at any given time. Saves on every change; debounced via React's input.
  // Clamps to a sensible 1–52 range so a typo can't release the whole plan.
  const onChangeDripWeeks = async (value) => {
    const n = Math.max(1, Math.min(52, parseInt(value, 10) || 4));
    setAthlete((a) => ({ ...a, drip_weeks: n }));
    try {
      const updated = await updateCoachingAthlete(id, { drip_weeks: n });
      setAthlete(updated);
    } catch {
      toast.error("Couldn't save drip release setting");
    }
  };

  // Pretty-print the active daily send time (override or default).
  const dailyTimeLabel = (() => {
    if (!athlete?.daily_email_enabled) return "Off";
    const t = (athlete.daily_email_time || "").trim();
    const time = t || "08:00";
    try {
      const [h, m] = time.split(":").map((n) => parseInt(n, 10));
      const d = new Date();
      d.setHours(h || 0, m || 0, 0, 0);
      const pretty = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
      return `Sends at ${pretty} daily${t ? "" : " (default)"}`;
    } catch {
      return `Sends at ${time} daily`;
    }
  })();

  // First 1-to-1 program the coach can preview today's email for.
  const previewablePid = (programs || []).find(
    (p) => p.program_type !== "generic"
  )?.id;

  const onInvite = async () => {
    if (!athlete?.email) {
      toast.error("Add an email to this athlete's profile first");
      return;
    }
    setInviteModalOpen(true);
  };

  const onInviteSent = async () => {
    // Refresh portal status so the button label switches to "Re-send invite"
    try {
      const m = await getPortalStatusesBulk();
      setPortalStatus((m || {})[id] || null);
    } catch {
      /* noop */
    }
  };

  const onTogglePaidActive = async () => {
    if (!athlete) return;
    const next = !(athlete.paid_active !== false);
    setTogglingPaid(true);
    try {
      const updated = await updateCoachingAthlete(id, { paid_active: next });
      setAthlete(updated);
      toast.success(next ? "Marked as Paid & Active" : "Marked as Unpaid — program locked");
    } catch {
      toast.error("Couldn't update payment status");
    } finally {
      setTogglingPaid(false);
    }
  };

  const [togglingActive, setTogglingActive] = useState(false);
  const onToggleActiveRoster = async () => {
    if (!athlete) return;
    const next = !(athlete.is_active !== false);
    setTogglingActive(true);
    try {
      const updated = await updateCoachingAthlete(id, { is_active: next });
      setAthlete(updated);
      toast.success(
        next
          ? "Marked as active — showing in Quick Jump again"
          : "Marked as inactive — hidden from Quick Jump (portal still accessible)"
      );
    } catch {
      toast.error("Couldn't update active status");
    } finally {
      setTogglingActive(false);
    }
  };
  const [showAssignPicker, setShowAssignPicker] = useState(false);
  // When set, opens the full AssignProgramModal which gives the coach week-
  // range + insert-into-existing options. Replaces the old one-click clone.
  const [pickedLibraryProgram, setPickedLibraryProgram] = useState(null);
  const [libraryPrograms, setLibraryPrograms] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    goal: "",
    target_race: "",
    total_weeks: 12,
    start_day: "mon",
    start_date: "",
  });
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, p] = await Promise.all([
        getCoachingAthlete(id),
        listPrograms({ athlete_id: id }),
      ]);
      setAthlete(a);
      setPrograms(p);
      setForm((f) => ({
        ...f,
        title: f.title || `${a.name} — Coaching Program`,
        goal: f.goal || a.goal || "",
        target_race: f.target_race || a.target_race || "",
      }));
      // Last digest summary (open/delivery)
      getLastDigest(id).then(setLastDigest).catch(() => setLastDigest(null));
      // Portal status (draft / invited / active)
      getPortalStatusesBulk()
        .then((m) => setPortalStatus((m || {})[id] || null))
        .catch(() => setPortalStatus(null));
      // Next auto-email schedule
      getNextDigest(id).then(setNextDigest).catch(() => setNextDigest(null));
      // Latest lactate test (null if no email match in the lactate collection)
      getCoachingAthleteLactate(id)
        .then(setLactateLink)
        .catch(() => setLactateLink(null));
      // Last portal activity timestamp (for the "Last seen" chip)
      getAthleteLastSeen(id)
        .then(setLastSeen)
        .catch(() => setLastSeen(null));
    } catch {
      toast.error("Couldn't load athlete");
      navigate("/coaching");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const p = await createProgram({
        program_type: "one_to_one",
        athlete_id: id,
        title: form.title,
        goal: form.goal,
        target_race: form.target_race,
        total_weeks: Number(form.total_weeks) || 0,
        start_day: form.start_day,
        start_date: form.start_date || null,
      });
      toast.success("Program created");
      navigate(`/coaching/programs/${p.id}/edit`);
    } catch {
      toast.error("Failed to create program");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (pid) => {
    if (!window.confirm("Delete this program?")) return;
    await deleteProgram(pid);
    toast.success("Program deleted");
    refresh();
  };

  const openAssignPicker = async () => {
    setShowAssignPicker(true);
    setLibraryLoading(true);
    try {
      const gen = await listPrograms({ program_type: "generic" });
      setLibraryPrograms(gen || []);
    } catch {
      toast.error("Couldn't load library");
    } finally {
      setLibraryLoading(false);
    }
  };

  const onAssignLibraryProgram = (libProgram) => {
    // Open the full AssignProgramModal so the coach can pick a week range
    // and (optionally) insert into an existing program. The modal handles
    // the API call + navigation itself.
    setPickedLibraryProgram(libProgram);
    setShowAssignPicker(false);
  };

  const onDeleteAthlete = async () => {
    if (!window.confirm(`Delete ${athlete?.name} and all their programs?`)) return;
    await deleteCoachingAthlete(id);
    toast.success("Athlete deleted");
    navigate("/coaching");
  };

  if (loading || !athlete) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-center text-[var(--tir-muted)]">
        Loading…
      </div>
    );
  }

  return (
    <div
      className="max-w-5xl mx-auto px-6 lg:px-10 py-10"
      data-testid="coaching-athlete-detail"
    >
      <Link
        to="/coaching"
        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors mb-6"
      >
        <ArrowLeft size={14} /> All coaching athletes
      </Link>

      <header className="border-b border-[var(--tir-ink)] pb-8 mb-6">
        <div className="min-w-0">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-3">
            Coaching · Athlete file
          </div>
          {editing && editForm ? (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl"
              data-testid="athlete-profile-edit-panel"
            >
              <div className="sm:col-span-2">
                <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">Name</label>
                <input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  data-testid="profile-edit-name"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-base focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="athlete@example.com"
                  data-testid="profile-edit-email"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none font-mono-tir"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">Phone</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="+1 555 555 5555"
                  data-testid="profile-edit-phone"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none font-mono-tir"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">Goal</label>
                <input
                  value={editForm.goal}
                  onChange={(e) => setEditForm({ ...editForm, goal: e.target.value })}
                  placeholder="Sub-4 hr marathon"
                  data-testid="profile-edit-goal"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">Target race</label>
                <input
                  value={editForm.target_race}
                  onChange={(e) => setEditForm({ ...editForm, target_race: e.target.value })}
                  placeholder="Boston Marathon — Apr 2026"
                  data-testid="profile-edit-target-race"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">Notes</label>
                <textarea
                  rows={2}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Anything internal you want to remember about this athlete"
                  data-testid="profile-edit-notes"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none leading-relaxed resize-y"
                />
              </div>

              {/* Payment gating — controls whether the athlete can access their
                  program in the portal + which Stripe link the invite email
                  points to. */}
              <div className="sm:col-span-2 mt-2 border-t border-[var(--tir-border)] pt-4">
                <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-burgundy)] mb-3">
                  Payment
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <label className="inline-flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={editForm.paid_active !== false}
                        onChange={(e) => setEditForm({ ...editForm, paid_active: e.target.checked })}
                        data-testid="profile-edit-paid-active"
                        className="h-5 w-5 mt-0.5 accent-[var(--tir-burgundy)]"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-[var(--tir-ink)]">
                          Paid &amp; active
                        </span>
                        <span className="block text-xs text-[var(--tir-muted)] leading-relaxed">
                          When on, athlete sees their full program. Turn off to lock their program behind a &ldquo;Complete payment&rdquo; screen.
                        </span>
                      </span>
                    </label>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
                      Payment link (override)
                      <span className="ml-2 text-[10px] tracking-normal text-[var(--tir-muted)]/80 normal-case font-normal">
                        (leave blank to use studio default)
                      </span>
                    </label>
                    <input
                      type="url"
                      value={editForm.payment_link}
                      onChange={(e) => setEditForm({ ...editForm, payment_link: e.target.value })}
                      placeholder="https://buy.stripe.com/..."
                      data-testid="profile-edit-payment-link"
                      className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none font-mono-tir"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
                      Payment note (coach-only)
                    </label>
                    <input
                      value={editForm.payment_note}
                      onChange={(e) => setEditForm({ ...editForm, payment_note: e.target.value })}
                      placeholder="e.g. Monthly e-transfer · paid via subscription · Custom Program"
                      data-testid="profile-edit-payment-note"
                      className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none"
                    />
                  </div>
                </div>
              </div>
              <div className="sm:col-span-2 flex items-center gap-2 mt-1">
                <button
                  type="button"
                  onClick={saveProfile}
                  disabled={savingProfile}
                  data-testid="profile-edit-save-btn"
                  className="inline-flex items-center gap-2 border border-[var(--tir-burgundy)] bg-[var(--tir-burgundy)] text-white px-4 py-2 text-xs tracking-[0.22em] uppercase font-semibold hover:bg-[#5e1521] disabled:opacity-60 transition-colors"
                >
                  <Save size={14} /> {savingProfile ? "Saving…" : "Save profile"}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={savingProfile}
                  data-testid="profile-edit-cancel-btn"
                  className="inline-flex items-center gap-2 border border-[var(--tir-border)] bg-white px-4 py-2 text-xs tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-ink)] hover:border-[var(--tir-ink)]"
                >
                  <X size={14} /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-serif-display text-5xl text-[var(--tir-ink)] leading-none">
                  {athlete.name}
                </h1>
                <LastSeenChip data={lastSeen} />
                <PaymentStatusBadge paid={athlete.paid_active !== false} size="md" />
                {athlete.is_active === false && (
                  <span
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold border text-[var(--tir-muted)] bg-[var(--tir-ivory)] border-[var(--tir-border)]"
                    data-testid="inactive-badge-header"
                    title="Coaching complete or paused — hidden from Quick Jump"
                  >
                    Inactive
                  </span>
                )}
                <button
                  type="button"
                  onClick={onTogglePaidActive}
                  disabled={togglingPaid}
                  data-testid="toggle-paid-active-btn"
                  title={athlete.paid_active !== false
                    ? "Athlete is active. Click to mark UNPAID and lock their program."
                    : "Athlete is locked. Click to mark PAID and unlock their program."}
                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] border border-[var(--tir-border)] hover:border-[var(--tir-burgundy)] px-2 py-1 transition-colors disabled:opacity-60"
                >
                  <DollarSign size={11} /> {athlete.paid_active !== false ? "Mark unpaid" : "Mark paid"}
                </button>
                <button
                  type="button"
                  onClick={onToggleActiveRoster}
                  disabled={togglingActive}
                  data-testid="toggle-active-roster-btn"
                  title={athlete.is_active !== false
                    ? "Currently coaching. Click to mark as inactive (hidden from Quick Jump; portal stays accessible)."
                    : "Coaching wrapped up. Click to mark active again."}
                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-navy)] border border-[var(--tir-border)] hover:border-[var(--tir-navy)] px-2 py-1 transition-colors disabled:opacity-60"
                >
                  {athlete.is_active !== false ? "Mark inactive" : "Mark active"}
                </button>
                <button
                  type="button"
                  onClick={openEdit}
                  data-testid="profile-edit-open-btn"
                  title="Edit profile (name, email, phone, goal, target race, notes, payment)"
                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] border border-[var(--tir-border)] hover:border-[var(--tir-burgundy)] px-2 py-1 transition-colors"
                >
                  <Pencil size={11} /> Edit
                </button>
              </div>
              {athlete.payment_note && (
                <div className="mt-2 text-xs text-[var(--tir-muted)] italic" data-testid="payment-note-display">
                  <span className="text-[10px] tracking-[0.22em] uppercase not-italic font-semibold text-[var(--tir-muted)] mr-2">Payment</span>
                  {athlete.payment_note}
                </div>
              )}
              <div className="flex flex-wrap gap-x-8 gap-y-2 mt-4 text-sm">
                {athlete.email ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="text-[var(--tir-muted)] font-mono-tir">{athlete.email}</span>
                    {lactateLink && lactateLink.athlete_id && (
                      <Link
                        to={`/athletes/${lactateLink.athlete_id}`}
                        data-testid="lactate-link-badge"
                        title={`Latest test: ${lactateLink.test_date || "on file"} — click to view`}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[var(--tir-navy)]/40 bg-[#e8eef2] text-[var(--tir-navy)] text-[9px] tracking-[0.18em] uppercase font-semibold hover:bg-[var(--tir-navy)] hover:text-white transition-colors"
                      >
                        <Link2 size={10} /> Linked to lactate
                      </Link>
                    )}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={openEdit}
                    data-testid="profile-missing-email-cta"
                    className="text-[var(--tir-burgundy)] font-semibold underline hover:text-[var(--tir-burgundy-dark)]"
                  >
                    + Add email
                  </button>
                )}
                {athlete.phone && (
                  <span className="text-[var(--tir-muted)] font-mono-tir">{athlete.phone}</span>
                )}
                {athlete.goal && (
                  <span>
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] mr-2">
                      Goal
                    </span>
                    <span className="text-[var(--tir-ink)]">{athlete.goal}</span>
                  </span>
                )}
                {athlete.target_race && (
                  <span>
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] mr-2">
                      Target
                    </span>
                    <span className="text-[var(--tir-ink)]">{athlete.target_race}</span>
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </header>
      {!editing && (
      <section
        className="mb-10 flex flex-wrap items-center gap-x-3 gap-y-2"
        data-testid="athlete-toolbar"
      >
        {(() => {
          const hasEmail = !!(athlete.email || "").trim();
          // When no email is on file, the button becomes an Edit-profile
            // shortcut: clicking it opens the inline edit panel so the coach
            // can add one without hunting for the pencil icon.
            if (!hasEmail) {
              return (
                <button
                  onClick={openEdit}
                  data-testid="invite-athlete-btn"
                  className="inline-flex items-center gap-2 border border-[var(--tir-burgundy)] text-[var(--tir-burgundy)] bg-white hover:bg-[var(--tir-burgundy)] hover:text-white px-4 py-2.5 text-sm tracking-wider uppercase font-medium transition-colors"
                  title="Add the athlete's email so you can send them portal access"
                >
                  <Mail size={16} /> Add email to invite
                </button>
              );
            }
            let label, disabled, title, variant;
            if (portalStatus === "active") {
              label = "Re-send access link";
              disabled = false;
              title = "Athlete is active. Send a fresh invite link if needed.";
              variant = "outline";
            } else if (portalStatus === "invited") {
              label = "Re-send invite";
              disabled = false;
              title = "Invite already sent — click to preview & re-send.";
              variant = "outline";
            } else {
              label = "Send invite email";
              disabled = false;
              title = "Preview + edit the invite email, then send.";
              variant = "primary";
            }
            const cls =
              variant === "primary"
                ? "border border-[var(--tir-burgundy)] bg-[var(--tir-burgundy)] text-white hover:bg-[#5e1521]"
                : "border border-[var(--tir-navy)] text-[var(--tir-navy)] bg-white hover:bg-[var(--tir-navy)] hover:text-white";
            return (
              <button
                onClick={onInvite}
                disabled={disabled}
                data-testid="invite-athlete-btn"
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm tracking-wider uppercase font-medium transition-colors disabled:opacity-60 ${cls}`}
                title={title}
              >
                <Mail size={16} /> {label}
              </button>
            );
          })()}
          <label
            className="flex flex-col items-start gap-0.5 px-3 py-1 text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-muted)] cursor-pointer select-none border border-transparent hover:text-[var(--tir-ink)] whitespace-nowrap"
            data-testid="weekly-email-toggle"
            title="Auto-email next week's workouts the day before each new week starts (08:00 your local time)."
          >
            <span className="inline-flex items-center gap-2 whitespace-nowrap">
              <input
                type="checkbox"
                checked={athlete.weekly_email_enabled !== false}
                onChange={onToggleWeeklyEmail}
                disabled={savingEmailToggle}
                className="h-4 w-4 accent-[var(--tir-burgundy)]"
              />
              Weekly preview email
            </span>
            <DigestStatusLine last={lastDigest} />
            <NextDigestLine next={nextDigest} />
          </label>
          <div
            className="flex flex-col items-start gap-1 px-3 py-1 text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-muted)] border border-transparent hover:text-[var(--tir-ink)] whitespace-nowrap"
            data-testid="daily-email-toggle-wrap"
          >
            <label className="inline-flex items-center gap-2 whitespace-nowrap cursor-pointer select-none" title="Email today's workout to the athlete every morning. Skips rest days. Off by default.">
              <input
                type="checkbox"
                checked={!!athlete.daily_email_enabled}
                onChange={onToggleDailyEmail}
                disabled={savingEmailToggle}
                data-testid="daily-email-toggle"
                className="h-4 w-4 accent-[var(--tir-navy)]"
              />
              Daily workout email
            </label>
            <div className="flex items-center gap-2 pl-6 normal-case tracking-normal">
              <span className="text-[10px] text-[var(--tir-muted)] font-mono-tir">at</span>
              <input
                type="time"
                value={athlete.daily_email_time || ""}
                placeholder="08:00"
                onChange={(e) => onChangeDailyTime(e.target.value)}
                disabled={!athlete.daily_email_enabled || savingEmailToggle}
                data-testid="daily-email-time-input"
                title="Override the send time for this athlete (24h). Leave blank for the default 08:00."
                className="text-[11px] font-mono-tir border border-[var(--tir-border)] hover:border-[var(--tir-ink)] bg-white px-1.5 py-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {previewablePid && (
                <button
                  type="button"
                  onClick={() =>
                    window.open(
                      getDailyPreviewUrl(previewablePid),
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                  data-testid="daily-email-preview-btn"
                  className="inline-flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] font-semibold border border-[var(--tir-burgundy)]/50 hover:border-[var(--tir-burgundy)] px-1.5 py-0.5"
                  title="Preview the email in a new tab — exactly what the athlete will see (does not send)"
                >
                  <MailOpen size={10} /> Preview
                </button>
              )}
            </div>
            <span className="font-mono-tir text-[9px] tracking-[0.12em] normal-case text-[var(--tir-muted)]/80 pl-6">
              {dailyTimeLabel}
            </span>
          </div>
          <div
            className="flex flex-col items-start gap-1 px-3 py-1 text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-muted)] border border-transparent hover:text-[var(--tir-ink)] whitespace-nowrap"
            data-testid="forgot-log-toggle-wrap"
          >
            <label
              className="inline-flex items-center gap-2 whitespace-nowrap cursor-pointer select-none"
              title="Automated reminder at 7 PM athlete-local on the last day of their week, listing every workout they haven't logged. Skipped silently if everything's logged."
            >
              <input
                type="checkbox"
                checked={!!athlete.forgot_log_reminder_enabled}
                onChange={onToggleForgotLog}
                disabled={savingEmailToggle}
                data-testid="forgot-log-toggle"
                className="h-4 w-4 accent-[var(--tir-burgundy)]"
              />
              Forgot-to-log reminder
            </label>
            <div className="flex items-center gap-2 pl-6 normal-case tracking-normal">
              <span className="text-[10px] text-[var(--tir-muted)] font-mono-tir">
                fires
              </span>
              <select
                value={athlete.forgot_log_reminder_day ?? 6}
                onChange={(e) => onChangeForgotLogDay(e.target.value)}
                disabled={
                  !athlete.forgot_log_reminder_enabled || savingEmailToggle
                }
                data-testid="forgot-log-day-select"
                title="The last day of this athlete's week — the reminder fires at 7 PM local on that day."
                className="text-[11px] font-mono-tir border border-[var(--tir-border)] hover:border-[var(--tir-ink)] bg-white px-1.5 py-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value={0}>Mon</option>
                <option value={1}>Tue</option>
                <option value={2}>Wed</option>
                <option value={3}>Thu</option>
                <option value={4}>Fri</option>
                <option value={5}>Sat</option>
                <option value={6}>Sun</option>
              </select>
              <span className="text-[10px] text-[var(--tir-muted)] font-mono-tir">
                at 7 PM
              </span>
              <button
                type="button"
                onClick={() =>
                  window.open(
                    getForgotLogPreviewUrl(id),
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
                data-testid="forgot-log-preview-btn"
                className="inline-flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] font-semibold border border-[var(--tir-burgundy)]/50 hover:border-[var(--tir-burgundy)] px-1.5 py-0.5"
                title="Preview the reminder in a new tab — exactly what the athlete will see for this week's un-logged workouts (does not send)"
              >
                <MailOpen size={10} /> Preview
              </button>
            </div>
            <span className="font-mono-tir text-[9px] tracking-[0.12em] normal-case text-[var(--tir-muted)]/80 pl-6">
              skips if everything is logged
            </span>
          </div>
          <div
            className="flex flex-col items-start gap-1 px-3 py-1 text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-muted)] border border-transparent hover:text-[var(--tir-ink)] whitespace-nowrap"
            data-testid="drip-weeks-wrap"
            title="How many weeks of the program this athlete can see at once (current week + upcoming). Past weeks always stay unlocked for review."
          >
            <span className="inline-flex items-center gap-2">
              Drip release
            </span>
            <div className="flex items-center gap-1.5 pl-0 normal-case tracking-normal">
              <input
                type="number"
                min={1}
                max={52}
                step={1}
                value={athlete.drip_weeks ?? 4}
                onChange={(e) => onChangeDripWeeks(e.target.value)}
                data-testid="drip-weeks-input"
                className="w-14 text-[11px] font-mono-tir border border-[var(--tir-border)] hover:border-[var(--tir-ink)] bg-white px-1.5 py-0.5"
              />
              <span className="text-[10px] text-[var(--tir-muted)] font-mono-tir normal-case tracking-normal">
                weeks visible
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowNew(true)}
            data-testid="new-program-btn"
            className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-5 py-2.5 text-sm tracking-wider uppercase font-medium hover:bg-[var(--tir-burgundy-dark)] transition-colors"
          >
            <Plus size={16} /> New program
          </button>
          <button
            onClick={onDeleteAthlete}
            data-testid="delete-coaching-athlete-btn"
            className="text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors"
          >
            Delete
          </button>
      </section>
      )}

      {showNew && (
        <form
          onSubmit={submit}
          className="mb-10 border border-[var(--tir-ink)] bg-white p-6"
          data-testid="new-program-form"
        >
          <div className="font-serif-display text-2xl mb-5">New program</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Program title
              </label>
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="program-title-input"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Goal
              </label>
              <input
                value={form.goal}
                onChange={(e) => setForm({ ...form, goal: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Target race
              </label>
              <input
                value={form.target_race}
                onChange={(e) => setForm({ ...form, target_race: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Total weeks
              </label>
              <input
                type="number"
                min="1"
                max="52"
                value={form.total_weeks}
                onChange={(e) => setForm({ ...form, total_weeks: e.target.value })}
                data-testid="program-weeks-input"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none font-mono-tir"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Week starts on
              </label>
              <select
                value={form.start_day}
                onChange={(e) => setForm({ ...form, start_day: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
              >
                <option value="mon">Monday (Mon–Sun)</option>
                <option value="sun">Sunday (Sun–Sat)</option>
                <option value="day1">Day 1 – Day 7</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Start date (optional)
              </label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                data-testid="program-start-date-input"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none font-mono-tir"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 mt-6">
            <button
              type="submit"
              disabled={saving}
              data-testid="submit-program-btn"
              className="bg-[var(--tir-burgundy)] text-white px-6 py-2.5 text-sm tracking-wider uppercase font-medium hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-50"
            >
              {saving ? "Creating…" : "Create program"}
            </button>
            <button
              type="button"
              onClick={() => setShowNew(false)}
              className="text-sm tracking-wider uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* PROGRAMS — promoted to a hero block so it doesn't get lost between
          drip-release controls above and the 4-week overview below.
          High-contrast ivory bg + heavy heading + larger program cards. */}
      <section
        className="mb-10 bg-[var(--tir-ivory)] rounded-lg border border-[var(--tir-border)] p-6 sm:p-8 shadow-sm"
        data-testid="coach-programs-section"
      >
        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <h2 className="font-serif-display text-4xl tracking-wide text-[var(--tir-ink)] leading-none">
              Programs
            </h2>
            <div className="text-[10px] tracking-[0.22em] uppercase font-mono-tir text-[var(--tir-muted)] mt-2">
              {programs.length} total · {athlete.name?.split(" ")[0]}'s training
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={openAssignPicker}
              data-testid="assign-from-library-btn"
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] px-3 py-2 border border-[var(--tir-burgundy)]/30 hover:border-[var(--tir-burgundy)] rounded-md transition-colors"
            >
              <Library size={13} /> Assign from library
            </button>
          </div>
        </div>
        {programs.length === 0 ? (
          <div className="bg-white rounded-md border-2 border-dashed border-[var(--tir-border)] py-16 text-center">
            <div className="font-serif-display text-2xl tracking-wide text-[var(--tir-ink)]">
              No programs yet
            </div>
            <div className="text-sm text-[var(--tir-muted)] mt-2">
              Create {athlete.name}'s first program to start planning weeks.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {programs.map((p) => (
              <div
                key={p.id}
                className="bg-white rounded-md border border-[var(--tir-border)] border-l-[5px] border-l-[var(--tir-burgundy)] hover:shadow-md hover:border-[var(--tir-navy)]/40 hover:border-l-[var(--tir-burgundy)] transition-all p-5 grid grid-cols-12 gap-4 items-center"
                data-testid={`program-row-${p.id}`}
              >
                <div className="col-span-12 md:col-span-6 min-w-0">
                  <div className="font-serif-display text-2xl tracking-wide text-[var(--tir-ink)] leading-tight">
                    {p.title}
                  </div>
                  <div className="text-[11px] tracking-[0.18em] uppercase font-mono-tir text-[var(--tir-muted)] mt-1.5">
                    {p.total_weeks} weeks
                    {p.target_race ? ` · ${p.target_race}` : ""}
                  </div>
                </div>
                <div className="col-span-6 md:col-span-3 text-sm text-[var(--tir-muted)]">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-mono-tir">Updated</div>
                  <div className="font-mono-tir mt-0.5">{formatDate(p.updated_at)}</div>
                </div>
                <div className="col-span-6 md:col-span-3 flex items-center justify-end gap-3 flex-wrap">
                  <Link
                    to={`/coaching/programs/${p.id}/preview`}
                    data-testid={`preview-program-${p.id}`}
                    className="inline-flex items-center gap-1 text-[11px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-ink)]"
                    title={`See exactly what ${athlete.name} sees on their portal`}
                  >
                    <Eye size={13} /> Preview
                  </Link>
                  <Link
                    to={`/coaching/programs/${p.id}/edit`}
                    data-testid={`edit-program-${p.id}`}
                    className="inline-flex items-center gap-1 text-[11px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)]"
                  >
                    <FileText size={13} /> Open
                  </Link>
                  <button
                    onClick={() => onDelete(p.id)}
                    data-testid={`delete-program-${p.id}`}
                    className="text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)]"
                    aria-label="Delete program"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Every coach⇄athlete conversation in one place — no more hunting
          through individual log modals. Deep-linked from the "left a note"
          push via ?thread={workout_id}. */}
      <AthleteConversationsSection athleteId={id} />

      <section className="mb-10">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-muted)]">
            4-week overview
          </h2>
          <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--tir-muted)]">
            Prescribed vs. actual · adherence trend
          </span>
        </div>
        <FourWeekOverview athleteId={id} />
      </section>

      <section className="mb-10" data-testid="coach-progress-section">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-muted)]">
            Progress
          </h2>
          <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--tir-muted)]">
            Exactly what the athlete sees on their Progress tab
          </span>
        </div>
        <CoachAthleteProgress athleteId={id} />
      </section>

      <section className="mb-10">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-muted)]">
            Athlete activity
          </h2>
          <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--tir-muted)]">
            Self-logged from the portal
          </span>
        </div>
        <AthleteActivityFeed athleteId={id} />
      </section>

      {/* Workout day-shifts the athlete has made on their own. Shows the
          coach when Morgen (etc.) moved a workout from one day to another
          within the same week so it's obvious why the plan looks off. */}
      <WorkoutShiftsBlock athleteId={id} />

      <section className="mb-10">
        <RaceCalendar athlete={athlete} onChange={(a) => setAthlete(a)} />
      </section>

      <section className="mb-10">
        <LactateProfile
          athlete={athlete}
          lactate={lactateLink}
          onAthleteChange={(a) => {
            setAthlete(a);
            // Re-fetch the lactate test since the email may have changed
            getCoachingAthleteLactate(a.id)
              .then(setLactateLink)
              .catch(() => setLactateLink(null));
          }}
        />
      </section>

      <section className="mb-10">
        <PaceProfile athlete={athlete} onChange={(a) => setAthlete(a)} />
      </section>

      <section className="mb-10">
        <StravaPanel athlete={athlete} onChange={refresh} />
      </section>

      <section className="mb-10">
        <CalendarFeedInspector athleteId={id} />
      </section>

      {showAssignPicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--tir-ink)]/50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowAssignPicker(false);
          }}
          data-testid="assign-library-picker-modal"
        >
          <div className="bg-[var(--tir-ivory)] border-2 border-[var(--tir-ink)] w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-1">
                  Assign from library
                </div>
                <h3 className="font-serif-display text-2xl text-[var(--tir-ink)]">
                  Pick a program for {athlete.name}
                </h3>
                <p className="text-xs text-[var(--tir-muted)] mt-1">
                  Creates a private copy. You can keep as many programs on this athlete as you like.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAssignPicker(false)}
                className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)] p-1"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {libraryLoading ? (
              <div className="py-12 text-center text-[var(--tir-muted)]">Loading library…</div>
            ) : libraryPrograms.length === 0 ? (
              <div className="py-10 text-center border border-dashed border-[var(--tir-border)]">
                <div className="font-serif-display text-xl text-[var(--tir-ink)]">
                  No library programs yet
                </div>
                <div className="text-sm text-[var(--tir-muted)] mt-2">
                  Build one first in the{" "}
                  <Link
                    to="/coaching/library"
                    className="underline hover:text-[var(--tir-burgundy)]"
                  >
                    Program Library
                  </Link>
                  .
                </div>
              </div>
            ) : (
              <ul className="divide-y divide-[var(--tir-border)] border-t border-b border-[var(--tir-border)]">
                {libraryPrograms.map((p) => (
                  <li
                    key={p.id}
                    className="py-3 flex items-center justify-between gap-3"
                    data-testid={`library-picker-row-${p.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-serif-display text-lg text-[var(--tir-ink)] leading-tight truncate">
                        {p.title}
                      </div>
                      <div className="text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] mt-0.5 flex flex-wrap gap-x-3">
                        <span>{p.total_weeks} weeks</span>
                        {p.experience_level && (
                          <span className="capitalize">{p.experience_level}</span>
                        )}
                        {p.max_mileage && <span>Peak {p.max_mileage}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onAssignLibraryProgram(p)}
                      data-testid={`assign-library-program-${p.id}`}
                      className="inline-flex items-center gap-1.5 bg-[var(--tir-burgundy)] text-white px-3 py-2 text-[10px] tracking-[0.22em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors"
                    >
                      <Plus size={11} /> Assign
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      {pickedLibraryProgram && (
        <AssignProgramModal
          program={pickedLibraryProgram}
          onClose={() => setPickedLibraryProgram(null)}
          onAssigned={(p) => {
            setPickedLibraryProgram(null);
            // Reload programs list and jump into the affected program
            toast.success("Program updated");
            navigate(`/coaching/programs/${p.id}/edit`);
          }}
          // Pre-select THIS athlete and skip the picker — coach is already
          // looking at this athlete's profile.
          lockedAthleteId={id}
        />
      )}
      <InviteAthleteModal
        athlete={athlete}
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSent={onInviteSent}
      />
    </div>
  );
}


/**
 * WorkoutShiftsBlock — coach-facing list of every workout day-shift the
 * athlete has made from their own portal. Shown on the athlete detail page
 * so the coach can see, at a glance, "Morgen moved Strength from Wed to Thu
 * yesterday" without opening the athlete-preview view. Empty state renders
 * nothing (self-hides) so it doesn't clutter profiles for athletes who
 * never move things.
 */
function WorkoutShiftsBlock({ athleteId }) {
  const [shifts, setShifts] = useState(null);
  useEffect(() => {
    let live = true;
    coachListWorkoutShifts(athleteId)
      .then((rows) => live && setShifts(rows || []))
      .catch(() => live && setShifts([]));
    return () => {
      live = false;
    };
  }, [athleteId]);
  if (!shifts || shifts.length === 0) return null;
  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };
  return (
    <section className="mb-10" data-testid="coach-workout-shifts-block">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-muted)]">
          Workouts athlete moved
        </h2>
        <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--tir-muted)]">
          Same-week reshuffles from their portal
        </span>
      </div>
      <div className="bg-white border border-[var(--tir-border)] rounded-md divide-y divide-[var(--tir-border)]">
        {shifts.map((s) => (
          <div
            key={s.id || `${s.week_number}-${s.workout_id}`}
            className="px-4 py-3 flex items-center gap-3 text-sm"
            data-testid={`coach-workout-shift-${s.workout_id}`}
          >
            <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-burgundy)]">
              Week {s.week_number}
            </span>
            <span className="text-[var(--tir-ink)] font-mono-tir">
              {s.from_day} <span className="text-[var(--tir-muted)]">→</span> {s.to_day}
            </span>
            <span className="ml-auto text-[11px] text-[var(--tir-muted)]">
              {fmtDate(s.shifted_at)}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
